import { dealerConfigureHostRestart } from '@racepointenergy/swc/scs/dealer-configure-host-restart'
import { useIntl } from 'react-intl'

import { Marking } from 'components/Mark'
import AvailabilityChecker from 'features/home/SysMonGuard/AvailabilityChecker'
import type { Home } from 'features/home/types'
import HomeUID from './HomeUID'
import SystemAccess from './HomeAccess/SystemAccess'
import MonitoringAccess from './HomeAccess/MonitoringAccess'
import HomeKebabMenu from './HomeAccess/HomeKebabMenu'
import { mainInfoHomeStatus } from './messages'
import HomeMetrics from './HomeMetrics'
import NotAvailableHost from '../NotAvailableHost'
import HostIcon from 'components/HostIcon'
import { notAvailableHostMessages } from './messages'
import { hasSystemAccessEnabled } from 'features/home/utils'

import {
  StyledMainInfoContainer,
  Header,
  DesktopHeaderTitle,
  LeftBlock,
  RightBlock,
  Body,
  MobileHeaderTitle,
  MarginMark,
  MobileMark,
  StatusWrapper,
} from './styles'

interface MainInfoProps {
  currentHome: Home
  isOnline: boolean
}

function MainInfo({ currentHome, isOnline }: MainInfoProps) {
  const { formatMessage } = useIntl()

  const [updateConfigureHostRestart, { isLoading }] =
    dealerConfigureHostRestart.useMutation()

  const getHomeStatusMessage = (homeStatus: boolean) =>
    homeStatus
      ? formatMessage(mainInfoHomeStatus.onlineHomeStatus)
      : formatMessage(mainInfoHomeStatus.offlineHomeStatus)

  const hasSystemAccess = hasSystemAccessEnabled(currentHome)

  return (
    <AvailabilityChecker homeId={currentHome.id}>
      {({ errors, isSysMonSupported, isAvailabilityLoading }) => {
        return (
          <>
            {currentHome && (
              <StyledMainInfoContainer>
                <Header>
                  <LeftBlock>
                    <MobileMark
                      marking={isOnline ? Marking.Check : Marking.Error}
                    />
                    <HostIcon home={currentHome} />
                    <DesktopHeaderTitle>{currentHome.name}</DesktopHeaderTitle>
                  </LeftBlock>

                  <RightBlock>
                    <StatusWrapper status={isOnline}>
                      <MarginMark
                        marking={isOnline ? Marking.Check : Marking.Error}
                      />
                      <p>{getHomeStatusMessage(isOnline)}</p>
                    </StatusWrapper>
                    {isSysMonSupported && hasSystemAccess && (
                      <HomeKebabMenu
                        homeId={currentHome.id}
                        updateConfigureHostRestart={updateConfigureHostRestart}
                        isLoading={isLoading}
                      />
                    )}
                  </RightBlock>
                </Header>

                <MobileHeaderTitle>{currentHome.name}</MobileHeaderTitle>

                <Body>
                  <HomeUID uid={currentHome.uid} />

                  <SystemAccess
                    id={currentHome.id}
                    name={currentHome.name}
                    domainId={currentHome.domainId}
                    configAccessExpires={currentHome.configAccessExpires}
                  />
                  <MonitoringAccess
                    id={currentHome.id}
                    name={currentHome.name}
                    domainId={currentHome.domainId}
                    monitorAccessEnabled={currentHome.monitorAccessEnabled}
                  />

                  {errors.length ? (
                    <NotAvailableHost
                      title={formatMessage(
                        notAvailableHostMessages.sysMonUnavailableTitle
                      )}
                      captions={errors}
                    />
                  ) : (
                    <>
                      {!isAvailabilityLoading && (
                        <HomeMetrics homeId={currentHome.id} />
                      )}
                    </>
                  )}
                </Body>
              </StyledMainInfoContainer>
            )}
          </>
        )
      }}
    </AvailabilityChecker>
  )
}

export default MainInfo
